import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router";
import { shallowEqual, useSelector } from "react-redux";
import moment from "moment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import NewBreadCrumbs from "../../../../../_metronic/layout/components/subheader/components/NewBreadCrumbs";
import SnackBarTool from "../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { isDebtCollectionSuperAdmin } from "../debt-collection/debtCollectionRoles";
import { saveDownloadResponse } from "../../../../utils/downloadExport";
import {
  checkConectiaFile,
  downloadConectiaReply,
  saveConectiaDecisions,
} from "../../_redux/conectiaValidation/conectiaValidationCrud";
import {
  VALIDATION_STATUSES,
  conflictingBookings,
  errorMessage,
  hasUnsavedDecisions,
  initialDecisions,
  isOverride,
  linesForSave,
  linesMissingReason,
  overridesForDownload,
  previousMonth,
  savedSnapshot,
  statusColor,
  statusLabel,
} from "./conectiaValidationHelpers";

/** Background for a pending line at 75+ days ("decide now"). */
const DECIDE_NOW_SX = { backgroundColor: "#fff4de" };

/** Background for a pending line past Conectia's 90-day pending limit. */
const PAST_LIMIT_SX = { backgroundColor: "#ffe2e5" };

/** Smaller grey second line inside a cell. */
const SECONDARY_SX = { display: "block", fontSize: "0.8rem", color: "#7e8299" };

/** Field names the file's columns are reported under, in display order. */
const COLUMN_LABELS = [
  ["reference", "Booking reference"],
  ["reqid", "Click id"],
  ["date", "Date"],
  ["value", "Value"],
  ["status", "Status"],
];

/**
 * A date as the app shows it, or "-".
 * @param {string|null|undefined} value - `YYYY-MM-DD`.
 * @returns {string} `DD-MM-YYYY` or "-".
 */
const showDate = (value) => (value ? moment(value).format("DD-MM-YYYY") : "-");

/**
 * Super-admin page for Conectia's monthly validation: upload Conectia's file,
 * see each line with its order and suggested status, change a status with a
 * reason, save the decisions and download the reply. The download waits for a
 * save of exactly the decisions on screen, so Conectia is never told something
 * the bookings don't hold. Redirects anyone else to the settings home (the API
 * refuses them too).
 * @returns {JSX.Element} The page, or a redirect.
 */
export function ConectiaValidationPage() {
  const roles = useSelector((state) => state.auth.user?.roles, shallowEqual);
  const isSuperAdmin = useMemo(() => isDebtCollectionSuperAdmin(roles), [roles]);

  const [file, setFile] = useState(null);
  const [activityMonth, setActivityMonth] = useState(previousMonth());
  // The checked lines plus the file and month they were checked with, so the
  // download sends exactly those (the overrides are keyed by that file's lines).
  const [checked, setChecked] = useState(null);
  const [decisions, setDecisions] = useState({});
  // savedSnapshot() of the last successful save for the checked file, or null.
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState("");
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });

  const result = checked?.result;
  const rows = useMemo(() => checked?.result?.rows || [], [checked]);
  const missingReason = useMemo(() => linesMissingReason(rows, decisions), [rows, decisions]);
  const conflicts = useMemo(() => conflictingBookings(rows, decisions), [rows, decisions]);
  // Save and download wait until every changed status has a reason and every
  // booking's lines agree (the save keeps one decision per booking).
  const blocked = missingReason.length > 0 || conflicts.length > 0;
  const unsaved = useMemo(() => hasUnsavedDecisions(rows, decisions, saved), [rows, decisions, saved]);

  if (!isSuperAdmin) {
    return <Navigate to="/setting-page" replace />;
  }

  /** Show a message in the snackbar. */
  const notify = (type, message) => {
    setSnack({ call: false, type: "", message: "" });
    setTimeout(() => setSnack({ call: true, type, message }), 0);
  };

  /**
   * Upload the chosen file and show the checked lines, keeping the file and
   * month they were checked with for the download.
   */
  const handleCheck = async () => {
    if (!file) {
      notify("error", "Choose Conectia's file first.");
      return;
    }
    const inputs = { file, activityMonth };
    setBusy("check");
    setSaved(null);
    try {
      const response = await checkConectiaFile(inputs.file, inputs.activityMonth);
      const data = response?.data?.data;
      setChecked(data ? { ...inputs, result: data } : null);
      setDecisions(initialDecisions(data?.rows));
    } catch (error) {
      setChecked(null);
      notify("error", await errorMessage(error, "The file could not be checked."));
    } finally {
      setBusy("");
    }
  };

  /** Change one line's status or reason. */
  const updateDecision = (line, change) => {
    setDecisions((current) => ({ ...current, [line]: { ...current[line], ...change } }));
  };

  /**
   * Store the decisions for matched bookings. The last save's snapshot is
   * dropped before the request: once a save is sent, the bookings may hold
   * either set of decisions (the server can commit and the reply still be
   * lost), so Download stays off until a save succeeds.
   */
  const handleSave = async () => {
    const lines = linesForSave(rows, decisions);
    const snapshot = savedSnapshot(rows, decisions);
    if (!lines.length) {
      notify("error", "No line matches a booking, so there is nothing to save.");
      return;
    }
    setSaved(null);
    setBusy("save");
    try {
      const response = await saveConectiaDecisions(lines);
      const outcome = response?.data?.data || {};
      setSaved(snapshot);
      notify("success", `Saved ${outcome.saved ?? lines.length} bookings (${outcome.overrides ?? 0} changed by hand).`);
    } catch (error) {
      notify("error", await errorMessage(error, "The decisions could not be saved."));
    } finally {
      setBusy("");
    }
  };

  /**
   * Download the reply file for Conectia, built from the file and month that
   * were checked (not whatever the inputs hold now), so the line-keyed
   * overrides match the lines on screen.
   */
  const handleDownload = async () => {
    if (!checked) return;
    setBusy("download");
    try {
      const response = await downloadConectiaReply(
        checked.file,
        checked.activityMonth,
        overridesForDownload(rows, decisions),
      );
      saveDownloadResponse(response, "conectia-validation-reply");
    } catch (error) {
      notify("error", await errorMessage(error, "The reply could not be built."));
    } finally {
      setBusy("");
    }
  };

  const deadline = result?.deadline;
  const summary = result?.summary;

  return (
    <>
      <NewBreadCrumbs title={"Conectia validation"} />

      <div className="card card-custom gutter-b">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label font-weight-bolder text-dark">Conectia monthly validation</span>
            <span className="text-muted mt-3 font-weight-bold font-size-sm">
              Upload Conectia&apos;s report, check each booking, save the decisions, then download the reply. It is due
              by the 15th of the month after the activity, or every tracked booking is billed.
            </span>
          </h3>
        </div>
        <div className="card-body pt-2">
          <div className="row align-items-end">
            <div className="col-md-5 form-group">
              <label>Conectia&apos;s file (CSV or XLSX)</label>
              <input
                type="file"
                className="form-control"
                accept=".csv,.xlsx"
                disabled={busy === "check"}
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setChecked(null);
                }}
              />
            </div>
            <div className="col-md-3 form-group">
              <label>Activity month</label>
              <input
                type="month"
                className="form-control"
                value={activityMonth}
                disabled={busy === "check"}
                onChange={(e) => setActivityMonth(e.target.value)}
              />
            </div>
            <div className="col-md-4 form-group">
              <button
                type="button"
                className="btn btn-primary font-weight-bolder"
                onClick={handleCheck}
                disabled={!file || Boolean(busy)}
              >
                Check file
                {busy === "check" && <span className="ml-2 mr-2 spinner spinner-white"></span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {result ? (
        <div className="card card-custom gutter-b">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label font-weight-bolder text-dark">
                {deadline?.passed
                  ? `Reply was due on ${showDate(deadline.due)} (${Math.abs(deadline.days_left)} days ago)`
                  : `Reply due by ${showDate(deadline?.due)} (${deadline?.days_left} days left)`}
              </span>
              <span className="text-muted mt-3 font-weight-bold font-size-sm">
                Activity {deadline?.activity_month}. {summary?.lines} lines: {summary?.accepted} accepted,{" "}
                {summary?.rejected} rejected, {summary?.pending} pending; {summary?.unmatched} not matched to a booking;{" "}
                {summary?.decide_now} to decide now; {summary?.needs_check} to check.
              </span>
              <span className="text-muted mt-2 font-size-sm">
                Columns used:{" "}
                {COLUMN_LABELS.map(([key, label]) => `${label}: ${result.columns?.[key] ? `"${result.columns[key]}"` : "not found"}`).join(" · ")}
              </span>
            </h3>
            <div className="card-toolbar">
              <button
                type="button"
                className="btn btn-primary font-weight-bolder mr-2"
                onClick={handleSave}
                disabled={Boolean(busy) || blocked}
              >
                Save decisions
                {busy === "save" && <span className="ml-2 mr-2 spinner spinner-white"></span>}
              </button>
              <button
                type="button"
                className="btn btn-primary font-weight-bolder"
                onClick={handleDownload}
                disabled={Boolean(busy) || blocked || unsaved}
              >
                Download reply
                {busy === "download" && <span className="ml-2 mr-2 spinner spinner-white"></span>}
              </button>
              {unsaved ? (
                <span className="text-muted font-size-sm ml-3" id="conectia-save-first">
                  {saved ? "Decisions changed since the last save: save again" : "Save the decisions first"}, so the
                  reply matches what the bookings hold.
                </span>
              ) : null}
            </div>
          </div>
          <div className="card-body pt-2">
            {checked.activityMonth !== activityMonth ? (
              <div className="text-warning mb-3">
                These lines were checked for {checked.activityMonth}, and the reply will use that month. Check the file
                again to use {activityMonth || "another month"}.
              </div>
            ) : null}
            {missingReason.length > 0 ? (
              <div className="text-danger mb-3">
                Give a reason for each changed status (line {missingReason.join(", ")}).
              </div>
            ) : null}
            {conflicts.length > 0 ? (
              <div className="text-danger mb-3">
                These bookings are on more than one line with different decisions. Give each booking&apos;s lines the
                same status and reason:
                <ul className="mb-0 mt-1">
                  {conflicts.map((booking) => (
                    <li key={booking.orderId}>
                      {booking.bookingReference}:{" "}
                      {booking.lines
                        .map(({ line, status, reason }) => `line ${line} ${statusLabel(status)}${reason ? ` ("${reason}")` : ""}`)
                        .join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" aria-label="Conectia validation lines">
                <TableHead>
                  <TableRow>
                    <TableCell>Line</TableCell>
                    <TableCell>Reference (file)</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Service date</TableCell>
                    <TableCell>Cancelled</TableCell>
                    <TableCell>Days since booking</TableCell>
                    <TableCell>Suggested</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Reason for change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const decision = decisions[row.line] || { status: row.suggested_status, reason: "" };
                    const changed = isOverride(row, decision);
                    const order = row.order;
                    const rowSx = row.past_pending_limit ? PAST_LIMIT_SX : row.decide_now ? DECIDE_NOW_SX : undefined;
                    return (
                      <TableRow key={row.line} sx={rowSx}>
                        <TableCell>{row.line}</TableCell>
                        <TableCell>{row.reference || "-"}</TableCell>
                        <TableCell>
                          {order ? (
                            <>
                              <Link to={`/order-details/${order.id}`}>{order.booking_reference}</Link>
                              <span style={SECONDARY_SX}>
                                {order.product}
                                {order.deleted ? `, deleted ${showDate(order.deleted_at)}` : ""}
                                {row.conversion ? "" : ", no Conectia click recorded"}
                              </span>
                            </>
                          ) : (
                            "No match"
                          )}
                        </TableCell>
                        <TableCell>{showDate(order?.service_date)}</TableCell>
                        <TableCell>{order?.cancelled ? showDate(order.cancel_date) : "-"}</TableCell>
                        <TableCell>
                          {row.days_since_booking ?? "-"}
                          {row.past_pending_limit ? (
                            <span style={SECONDARY_SX}>Past the 90-day pending limit</span>
                          ) : row.decide_now ? (
                            <span style={SECONDARY_SX}>Decide now (pending allowed until {showDate(row.pending_until)})</span>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <span className={`label label-lg label-light-${statusColor(row.suggested_status)} label-inline`}>
                            {statusLabel(row.suggested_status)}
                          </span>
                          {row.needs_check ? (
                            <span className="label label-lg label-light-dark label-inline ml-1">Check</span>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {row.reason}
                          {(row.notes || []).map((note) => (
                            <span key={note} style={SECONDARY_SX}>
                              {note}
                            </span>
                          ))}
                        </TableCell>
                        <TableCell>
                          <select
                            className="form-control form-control-sm"
                            value={decision.status}
                            onChange={(e) => updateDecision(row.line, { status: e.target.value })}
                            aria-label={`Decision for line ${row.line}`}
                          >
                            {VALIDATION_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {statusLabel(status)}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell>
                          {changed ? (
                            <input
                              type="text"
                              className={`form-control form-control-sm ${decision.reason.trim() ? "" : "is-invalid"}`}
                              maxLength={500}
                              value={decision.reason}
                              placeholder="Why the change"
                              onChange={(e) => updateDecision(row.line, { reason: e.target.value })}
                              aria-label={`Reason for changing line ${row.line}`}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      ) : null}

      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </>
  );
}

export default ConectiaValidationPage;
